import { useEffect, useState } from 'react';

/**
 * Subscribe to a CSS media query from React.
 *
 * Layout belongs in CSS, and almost everywhere in this portal it stays there —
 * a `md:hidden` is cheaper and cannot go out of step with the stylesheet. This
 * hook is for the handful of places where a *behaviour* differs, not just an
 * appearance: the website inbox auto-opens the first conversation on a wide
 * screen, and must not on a phone, where that would mean never seeing the list.
 *
 * `fallback` is what to answer when the browser cannot be asked — a server
 * render, or jsdom, which has no `matchMedia`. It defaults to true so callers
 * asking "is this a wide screen?" get the desktop behaviour that has always
 * been there, rather than a silently different one under test.
 */
export default function useMediaQuery(query, fallback = true) {
  const read = () => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return fallback;
    }
    return window.matchMedia(query).matches;
  };

  const [matches, setMatches] = useState(read);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }
    const list = window.matchMedia(query);
    const onChange = (event) => setMatches(event.matches);
    setMatches(list.matches);
    // addEventListener is the modern spelling; addListener is kept for older
    // WebKit, which is still what a fair number of iPhones are running.
    if (typeof list.addEventListener === 'function') {
      list.addEventListener('change', onChange);
      return () => list.removeEventListener('change', onChange);
    }
    list.addListener(onChange);
    return () => list.removeListener(onChange);
  }, [query]);

  return matches;
}

/** The portal's `lg` breakpoint — where the sidebar stops being a drawer. */
export const DESKTOP_QUERY = '(min-width: 1024px)';
